// src/hooks/useGenerationQueue.js
// Global generation queue management with Firestore real-time listeners

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import apiService from '../apiService';
import { API_BASE_URL } from '../config/runtime';
import { jobCompletionEmitter } from '../events/jobCompletion';
import { GENERIC_ERROR_MESSAGE } from '../utils/constants';
import { DEV_EMPTY_SECONDS, DEV_FAIL_SECONDS } from '../components/composerConfig';
import {
  dropUnscoped, pruneForeign, readUserJSON, writeUserJSON,
} from '../utils/perUserStorage';

const resolveVideoUrl = (url) =>
  url && url.startsWith("/api/") ? `${API_BASE_URL}${url}` : url;

/* ONE BUCKET PER SIGNED-IN USER, never one per device. This used to be a single key with no
   uid in it, and nothing cleared it at sign-out — so logging into a second account on the same
   machine rendered the first account's finished generation as a tile that could never be
   replaced (the Workspace swaps such a tile for its video once that video appears in YOUR
   library, and it never would). See utils/perUserStorage.js. */
const QUEUE_STORE = 'magi_generation_queue';
// How long another account's leftovers stay on this device before they are swept. Long enough
// to survive a day of switching back and forth, short enough that a shared machine does not
// accumulate strangers' job titles forever.
const FOREIGN_QUEUE_TTL = 24 * 60 * 60 * 1000;
const STALE_TIMEOUT = 20 * 60 * 1000; // 20 minutes — kill listener if no update
/* TWO MINUTES OF SILENCE AND WE STOP WAITING.
 *
 * NOT a cap on how long a video may take — a legitimate render reports as it goes (narration
 * done, each scene finished) and may well run longer than two minutes. This is the gap
 * BETWEEN those reports, and it resets on every one. A job starved of GPUs says nothing at
 * all: bradkashani's three went quiet straight after narration and the next word anyone heard
 * was six and a half minutes later, three times over, so his card counted up for nineteen
 * minutes before admitting anything was wrong.
 *
 * Two minutes is longer than the slowest single step we actually observe (the whole pipeline
 * averages ~5m across many steps), so a healthy job never trips it; a wedged one is reported
 * while the person is still looking at the screen. */
const SILENT_TIMEOUT = 2 * 60 * 1000;
/* A REEL IS QUIET FOR MUCH LONGER THAN THAT, LEGITIMATELY.
 *
 * The number above is calibrated on a lesson, which runs straight through and reports after
 * every step. A reel runs in ROUNDS — it writes several drafts, renders them, scores them
 * against each other, then refines the winner — and a round reports nothing until it finishes.
 * So a perfectly healthy reel goes silent for stretches a lesson never does, and the two-minute
 * clock failed the CARD while the job itself carried on and finished on the server. The person
 * is told their reel died and then finds it in the dashboard, which is worse than either
 * outcome on its own.
 *
 * Same reason `worker/handle_job._cap_seconds` already exempts reels from the three-minute
 * server-side cap: a reel's wall-clock has no single sensible ceiling. This is the client half
 * of that decision, and it is a longer clock rather than no clock because a wedged reel should
 * still eventually say so rather than breathe at you forever — 15 minutes is past the slowest
 * real round and still inside the 20-minute STALE_TIMEOUT that backstops everything. */
const REEL_SILENT_TIMEOUT = 15 * 60 * 1000;
// Orphan sweep: how often to ask the API whether a long-running job still exists, and how old
// a job must be before it is worth asking. The grace period is comfortably past the slowest
// normal generation (measured p90 ~114s), so a healthy job is never questioned.
const ORPHAN_SWEEP_MS = 30 * 1000;
const ORPHAN_GRACE_MS = 4 * 60 * 1000;

// Job states
const JOB_STATES = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  // WAITING ON A HUMAN, which is not a kind of processing and emphatically not a failure.
  // A job set to "Manual — I pick" writes its candidates, sets awaiting_selection and stops;
  // nothing is in flight, so no further update arrives. Without this state it fell through
  // to PROCESSING and then hit the stale timeout below, and a job sitting there waiting for
  // you was reported as "Generation timed out" after 20 minutes.
  AWAITING: 'awaiting_selection',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

// States that are legitimately quiet forever. The stale timeout exists to catch a job whose
// backend died mid-run; a parked job has not died, it is holding.
const TERMINAL_OR_WAITING = new Set([
  JOB_STATES.COMPLETED, JOB_STATES.FAILED, JOB_STATES.CANCELLED, JOB_STATES.AWAITING,
]);

export const useGenerationQueue = (options = {}) => {
  const [queue, setQueue] = useState([]);
  const [isMinimized, setIsMinimized] = useState(true);
  const subscriptions = useRef({});  // jobId -> unsubscribe function
  const timeouts = useRef({});       // jobId -> stale timeout ID
  /* jobId -> how long this job may go quiet, in ms.
   *
   * A REF, NOT READ OFF THE QUEUE, because `armSilenceTimer` runs from two places that have
   * only an id: `subscribeToJob`, before any snapshot has arrived, and `handleJobUpdate`,
   * which arms the clock BEFORE its own `setQueue` — so the job object either does not exist
   * yet or is one render behind. Written wherever the kind is first known (a launch, a
   * rehydrate, a snapshot that says `from_reel_editor`) and only ever upgraded, never
   * lowered: nothing that arrives later can turn a reel back into a lesson. */
  const silenceBudget = useRef({});
  /* Jobs whose completion we have already acted on.
   *
   * A REF, AND THAT IS THE WHOLE FIX. This used to be a plain variable assigned INSIDE the
   * `setQueue` updater and read on the line after it — and React only runs an updater
   * synchronously when the component has no other update pending (`dispatchSetState`'s
   * eager-state path). With anything else in flight — a second snapshot, another job, the
   * 30s orphan sweep — the updater was deferred to render, the variable was still null when
   * it was read, the finalizer never ran, and the listener had ALREADY been torn down. The
   * card sat at "processing" for ever on a job the backend had finished; checked against
   * production, `job_progress.status` read `completed` on every one of the last eight
   * videos, so nothing was ever lost on the way here.
   *
   * A ref is not subject to any of that. "Have we finalized this job" is bookkeeping about
   * work WE have done, so it is kept where React's scheduler cannot skip it, and the updater
   * goes back to being a pure function of (previous queue, snapshot).
   *
   * Seeded on rehydrate from `backendCompleted` so the non-idempotent thumbnail work is not
   * paid for twice across a reload — the guard `alreadyFinalized` used to provide. */
  const finalized = useRef(new Set());
  /* THE QUEUE, READABLE WITHOUT AN UPDATER. Finalization needs the job's `type` and
     `metadata` — App's `onJobCompleted` refreshes a course from `metadata.courseId`, and the
     completion event carries the type — and those live on the queue entry, not on the
     snapshot (`job_progress` has never carried either). Reaching them used to mean doing the
     work inside `setQueue`, which is the bug this whole change removes.
     Being one render stale is harmless here: `type` and `metadata` are written once by
     `addJob` and never change for the life of the job. */
  const queueRef = useRef([]);
  const projectDataRef = useRef({});
  /* jobId -> { status, videoId } for EVERY job we hear about, in or out of `queue`.
   *
   * A project's sub-jobs are subscribed to but deliberately never added to `queue` — they
   * must not render as their own tiles, or a two-problem homework set would show as two
   * loose videos instead of one group. The aggregator then had nothing to count: it looked
   * its sub-jobs up IN `queue` with `prev.find(j => j.id === subId)`, got undefined for every
   * one, and concluded 0 of 2 were done on every tick. `allDone` could never be true, so the
   * project tile stayed PROCESSING after both videos were finished and written — forever,
   * including across a reload, since the rehydrate path re-arms the same broken aggregation.
   *
   * So the sub-job states live here instead, written by `handleJobUpdate` for every job id it
   * is called with. This is the only record of a job that is watched but not displayed. */
  const jobStatusRef = useRef({});
  /* WHOSE QUEUE THIS IS. Null while auth is still settling and for a signed-out visitor —
     neither can own work, so neither gets a queue. */
  const { onJobCompleted, userId = null } = options;

  // ─── Firestore Listener Core ───────────────────────────────────────

  /* Re-armed on every update, so the clock measures SILENCE rather than total duration.
     Declared before `handleJobUpdate`, which re-arms it. `unsubscribeFromJob` is referenced
     inside the callback only — by the time a timer fires, the whole body has run. */
  /** Put a job on the reel clock if it is a reel. Only ever upgrades.
   *
   *  Called from every place the kind becomes known. `Math.max` rather than an assignment
   *  because the sources disagree in one direction only: a launch knows it is a reel, while a
   *  later snapshot of the same job carries no `duration_type` at all (the `job_progress`
   *  document has never held one), and plain assignment on a second call would quietly put a
   *  running reel back on the two-minute clock.
   */
  const setSilenceBudget = useCallback((jobId, durationType) => {
    if (!jobId) return;
    const want = String(durationType || '').toLowerCase() === 'reel'
      ? REEL_SILENT_TIMEOUT : SILENT_TIMEOUT;
    silenceBudget.current[jobId] = Math.max(silenceBudget.current[jobId] || 0, want);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const armSilenceTimer = useCallback((jobId) => {
    if (timeouts.current[jobId]) clearTimeout(timeouts.current[jobId]);
    // CAPTURED AT ARM TIME, both of them. The budget because the timer must fire on the clock
    // it was armed with, and the sentence because it is built FROM the budget — the number in
    // the message and the number in the timer are the same value, so they cannot drift apart
    // the way a hardcoded "2 minutes" did the moment a second clock existed.
    const budget = silenceBudget.current[jobId] || SILENT_TIMEOUT;
    const minutes = Math.round(budget / 60000);
    timeouts.current[jobId] = setTimeout(() => {
      unsubscribeFromJob(jobId);
      setQueue(prev => prev.map(job =>
        job.id === jobId && !TERMINAL_OR_WAITING.has(job.state)
          ? { ...job,
              state: JOB_STATES.FAILED,
              completedAt: new Date().toISOString(),
              // Phrased for `failureReasonFor`, which turns it into one plain sentence.
              error: `No progress for ${minutes} minutes while rendering — we stopped waiting.` }
          : job
      ));
    }, budget);
  }, []);

  const handleJobUpdate = useCallback((jobId, data) => {
    /* RECORD FIRST, before the queue map — because the queue map is a no-op for any job that
       is not in `queue`, which is exactly the case this record exists to cover. Raw backend
       status, not the mapped local state, because the aggregator wants the backend's answer.
       (It used to say `completed` is held at PROCESSING until the finalizer runs — that hold
       is gone; the card now reaches COMPLETED in the updater itself.) */
    if (data?.status) {
      jobStatusRef.current[jobId] = {
        status: data.status,
        videoId: data.video_id || jobStatusRef.current[jobId]?.videoId || null,
      };
    }

    /* THE SILENCE CLOCK RESTARTS HERE. Any word from the backend — a step, a scene, a
       progress percentage — means the job is alive, so the two minutes begin again. A job
       that has reached a terminal or waiting state is not expected to say anything more and
       must not be failed for going quiet. */
    /* THE SNAPSHOT'S ONE RELIABLE REEL SIGNAL. `job_progress` carries no `duration_type` —
       its fields are status/progress/scenes/title and a handful of reel flags — so this is
       the only kind a job can declare about itself after the fact. It covers a Fix / Redraw /
       Refine fired from the reel editor; a reel BEING GENERATED is tagged at `addJob`
       instead. Harmless when it arrives late: the budget only upgrades, and the next update
       re-arms the clock on the longer one. */
    if (data?.from_reel_editor) setSilenceBudget(jobId, 'reel');

    if (TERMINAL_OR_WAITING.has(data?.status)) {
      if (timeouts.current[jobId]) {
        clearTimeout(timeouts.current[jobId]);
        delete timeouts.current[jobId];
      }
    } else {
      armSilenceTimer(jobId);
    }

    setQueue(prevQueue => prevQueue.map(job => {
      if (job.id !== jobId) return job;

      const scenes = data.scenes || job.scenes || null;
      const pipelineMode = data.pipeline_mode || job.pipelineMode || null;
      const totalScenes = data.total_scenes || job.totalScenes || null;
      const rawProgress = data.progress ?? job.progress;

      let localState;
      if (data.status === 'completed') {
        /* COMPLETED, IMMEDIATELY. This used to hold the card at PROCESSING until the Phase 2
           block below had run, so that the thumbnail was pinned before the card changed
           appearance. That made the one thing the user is waiting for — "my video is done" —
           depend on a library refresh that takes 3.5s of deliberate sleeps, three round
           trips, and historically never finished at all. The flip is the product; the
           refresh is a nice-to-have, and it still happens a beat later. */
        localState = JOB_STATES.COMPLETED;
      } else if (data.status === 'failed') {
        localState = JOB_STATES.FAILED;
      } else if (data.status === 'queued') {
        localState = JOB_STATES.QUEUED;
      } else if (data.status === 'cancelled') {
        localState = JOB_STATES.CANCELLED;
      } else if (data.status === JOB_STATES.AWAITING) {
        localState = JOB_STATES.AWAITING;
      } else {
        localState = JOB_STATES.PROCESSING;
      }

      const newJob = {
        ...job,
        progress: rawProgress,
        currentStep: data.current_step || job.currentStep,
        state: localState,
        processingStartedAt: job.processingStartedAt
          || (localState === JOB_STATES.PROCESSING ? new Date().toISOString() : undefined),
        title: data.title || job.title,
        videoId: data.video_id || job.videoId || null,
        videoUrl: resolveVideoUrl(data.video_url),
        promptCorpus: data.prompt_corpus || job.promptCorpus || null,
        // HOW LONG THIS JOB SHOULD TAKE, decided by the backend because it depends on the
        // renderer in the job's prompt package and the client cannot see that. Absent for
        // anything whose renderer could not be established, which is when the countdown
        // falls back to its own default. Sticky for the same reason as solveFirst below: it
        // rides the job's request, so a snapshot that omits it must not change the clock
        // halfway through a generation.
        estimatedSeconds: data.estimated_seconds || job.estimatedSeconds || null,
        // STICKY, like fromReelEditor below and for the same reason: the flag rides the
        // job's request, and a snapshot that happens to omit it must not turn a solved reel
        // back into an unsolved one halfway through.
        solveFirst: data.solve_first || job.solveFirst || false,
        // A reel-editor Fix / Redraw / Refine rather than a reel being generated. Sticky once
        // true: the flag rides the job's request, and a snapshot that happens to omit it must
        // not turn an edit back into a generation halfway through.
        fromReelEditor: data.from_reel_editor || job.fromReelEditor || false,
        subtitle_url: data.subtitle_url || job.subtitle_url || null,
        error: data.error || null,
        pipelineMode,
        totalScenes,
        scenes,
        completedAt: (localState === JOB_STATES.COMPLETED || localState === JOB_STATES.FAILED)
          ? (job.completedAt || new Date().toISOString())
          : job.completedAt,
        backendCompleted: job.backendCompleted || (data.status === 'completed'),
      };

      /* NOTHING IS ASSIGNED OUT OF THIS UPDATER. It is a pure (previous, snapshot) -> next;
         everything that has to HAPPEN on completion is below, outside it. See `finalized`. */
      return newJob;
    }));

    // Terminal states: unsubscribe listener. SAFE TO DO HERE now that the flip to COMPLETED
    // happens in the updater above rather than in the block below — the card is already in
    // its final state by the time the listener goes away. When the two were the other way
    // round, tearing down beside a finalizer that could be skipped is what made the hang
    // unrecoverable: no further snapshot could ever arrive to put it right.
    if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
      unsubscribeFromJob(jobId);
    }

    if (data.status === 'failed') {
      scheduleFailedDismiss(jobId);
    }

    /* Phase 2 — EVERYTHING THAT IS NOT THE CARD. Refresh the library, pin the thumbnail,
       announce the job. None of it gates the card any more, so a slow or failed refresh can
       no longer hold a finished video hostage.
       Keyed on the ref rather than on the previous state, and claimed BEFORE the await so a
       second `completed` snapshot arriving mid-flight cannot start a second copy. */
    if (data.status === 'completed' && !finalized.current.has(jobId)) {
      finalized.current.add(jobId);
      const known = queueRef.current.find((j) => j.id === jobId) || {};
      const finishedJob = {
        ...known,
        id: jobId,
        // The snapshot is newer than the mirror for anything it actually carries.
        title: data.title || known.title || null,
        videoId: data.video_id || known.videoId || null,
        videoUrl: resolveVideoUrl(data.video_url) || known.videoUrl || null,
        type: known.type || 'video',
      };
      /* ANNOUNCE FIRST, REFRESH SECOND, and the order is the point.
       *
       * The emit used to sit AFTER an awaited `onJobCompleted`, raced against a 15s timeout —
       * so every listener learned the job had finished only once the library refresh had come
       * back, or 15 seconds later if it had not. The session page is one of those listeners,
       * and what it does on the signal is re-read its own session, which is the fetch that
       * carries the thumbnail paths. So a finished lesson could sit under the loading loop for
       * the whole of someone else's slow request before anything went looking for its still.
       *
       * Nothing in the emit depends on the refresh: it carries only what the snapshot already
       * told us. Sent synchronously here, the listeners start their own work immediately and
       * the refresh runs beside them instead of in front of them. */
      jobCompletionEmitter.emit(jobId, {
        type: finishedJob.type,
        title: finishedJob.title,
        videoUrl: finishedJob.videoUrl,
      });

      (async () => {
        try {
          if (onJobCompleted) {
            await Promise.race([
              onJobCompleted(finishedJob),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('onJobCompleted timeout')), 15000)
              ),
            ]);
          }
        } catch (e) {
          console.error('onJobCompleted failed or timed out:', e.message);
        }
      })();
    }
  }, [onJobCompleted, armSilenceTimer, setSilenceBudget]);

  const subscribeToJob = useCallback((jobId) => {
    if (subscriptions.current[jobId]) {
      subscriptions.current[jobId]();
    }
    if (timeouts.current[jobId]) {
      clearTimeout(timeouts.current[jobId]);
    }

    if (!db) {
      // Firestore unavailable — fall back to single REST poll with retry
      pollJobFallback(jobId);
      return;
    }

    const docRef = doc(db, 'job_progress', jobId);
    let hasReceivedData = false;

    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Doc not written yet — backend may be slightly behind.
        // Wait briefly, then try a one-shot REST poll as fallback.
        if (!hasReceivedData) {
          setTimeout(() => {
            if (!hasReceivedData) pollJobFallback(jobId);
          }, 5000);
        }
        return;
      }
      hasReceivedData = true;
      const data = snapshot.data();
      handleJobUpdate(jobId, data);
    }, (error) => {
      console.error(`Firestore listener error for ${jobId}:`, error);
      // Fall back to REST polling on listener failure
      pollJobFallback(jobId);
    });

    subscriptions.current[jobId] = unsubscribe;

    // The silence clock starts here and is re-armed by every update that arrives.
    armSilenceTimer(jobId);
  }, [handleJobUpdate, armSilenceTimer]);

  const unsubscribeFromJob = useCallback((jobId) => {
    if (subscriptions.current[jobId]) {
      subscriptions.current[jobId]();
      delete subscriptions.current[jobId];
    }
    if (timeouts.current[jobId]) {
      clearTimeout(timeouts.current[jobId]);
      delete timeouts.current[jobId];
    }
  }, []);

  // ─── REST Fallback (single poll, not recurring) ────────────────────

  const pollJobFallback = useCallback(async (jobId) => {
    try {
      const status = await apiService.getVideoStatus(jobId);
      handleJobUpdate(jobId, status);

      // If still active after fallback poll, retry subscription or poll again later
      if (status.status === 'processing' || status.status === 'queued') {
        if (db) {
          // Retry Firestore subscription
          setTimeout(() => subscribeToJob(jobId), 3000);
        } else {
          // No Firestore — schedule another REST poll
          timeouts.current[jobId] = setTimeout(() => pollJobFallback(jobId), 5000);
        }
      }
    } catch (error) {
      console.error(`Fallback poll failed for ${jobId}:`, error);
      // Retry once more after delay
      timeouts.current[jobId] = setTimeout(() => {
        apiService.getVideoStatus(jobId)
          .then(s => handleJobUpdate(jobId, s))
          .catch(() => {
            setQueue(prev => prev.map(job =>
              job.id === jobId && (job.state === JOB_STATES.QUEUED || job.state === JOB_STATES.PROCESSING)
                ? { ...job, state: JOB_STATES.FAILED, error: GENERIC_ERROR_MESSAGE, completedAt: new Date().toISOString() }
                : job
            ));
            scheduleFailedDismiss(jobId);
          });
      }, 10000);
    }
  }, [handleJobUpdate, subscribeToJob]);

  // ─── Project Jobs (aggregate sub-jobs) ─────────────────────────────

  const subscribeToProject = useCallback((projectId) => {
    const pd = projectDataRef.current[projectId];
    if (!pd) return;

    // Subscribe to each sub-job — aggregate state in a shared handler
    pd.subJobIds.forEach(subId => {
      subscribeToJob(subId);
    });

    // Override the per-job handler: aggregate into the project entry
    const checkProjectCompletion = () => {
      setQueue(prev => {
        const project = prev.find(j => j.id === projectId);
        if (!project) return prev;

        // Count completed/failed from sub-job states in the queue
        let completedCount = 0;
        let failedCount = 0;
        let firstReadyVideoId = null;

        /* `jobStatusRef` FIRST, `queue` only as a fallback. Sub-jobs are never added to the
           queue (see the ref's declaration), so the lookup that used to be the only source
           found nothing and the project could never finish. The queue branch stays because a
           job can legitimately be both watched and displayed — the dev "Empty" mode drives a
           queue entry with no backend and so writes no status record at all. */
        pd.subJobIds.forEach(subId => {
          const rec = jobStatusRef.current[subId];
          if (rec) {
            if (rec.status === 'completed') {
              completedCount++;
              if (!firstReadyVideoId) firstReadyVideoId = rec.videoId;
            } else if (rec.status === 'failed' || rec.status === 'cancelled') {
              failedCount++;
            }
            return;
          }
          const subJob = prev.find(j => j.id === subId);
          if (subJob) {
            if (subJob.state === JOB_STATES.COMPLETED) {
              completedCount++;
              if (!firstReadyVideoId) firstReadyVideoId = subJob.videoId;
            } else if (subJob.state === JOB_STATES.FAILED) {
              failedCount++;
            }
          }
        });

        const allDone = (completedCount + failedCount) === pd.subJobIds.length;

        /* STOP WHEN IT IS DONE. The aggregate interval used to run every 5s for the life of
           the page, which was invisible only because `allDone` could never become true — now
           that it can, a finished project would otherwise keep re-rendering the queue forever.
           Dropping the sub-jobs' status records here keeps the ref from growing across a long
           session; the project tile has already taken what it needs from them. */
        if (allDone) {
          clearInterval(timeouts.current[projectId]);
          delete timeouts.current[projectId];
          pd.subJobIds.forEach(subId => { delete jobStatusRef.current[subId]; });
        }

        return prev.map(job => {
          if (job.id !== projectId) return job;
          return {
            ...job,
            videoId: firstReadyVideoId || job.videoId || null,
            state: allDone
              ? (failedCount === pd.subJobIds.length ? JOB_STATES.FAILED : JOB_STATES.COMPLETED)
              : JOB_STATES.PROCESSING,
            completedCount,
            failedCount,
            totalCount: pd.subJobIds.length,
            completedAt: allDone ? (job.completedAt || new Date().toISOString()) : job.completedAt,
          };
        });
      });
    };

    // Poll aggregation every 5s (lightweight — just reads local queue state)
    const aggregateInterval = setInterval(checkProjectCompletion, 5000);
    timeouts.current[projectId] = aggregateInterval;

    // Also run immediately
    setTimeout(checkProjectCompletion, 1000);
  }, [subscribeToJob]);

  // ─── Lifecycle ─────────────────────────────────────────────────────

  /* A FAILURE IS NOT DISMISSED BY A TIMER ANY MORE.
   *
   * This used to delete a failed job from the queue sixty seconds after it failed, and the
   * Workspace never rendered failed jobs at all — so the card you were watching simply
   * VANISHED. You came back to a grid with nothing in it and no way to find out what had
   * happened: no message, no row, no record. Three generations died that way this morning
   * and the only trace was in the admin log.
   *
   * A failure now stays until the person says they have seen it (the × on the card, or the
   * banner's dismiss, both of which call `removeJob`). Kept as a no-op rather than deleted
   * because three call sites hand it a job id and the name says what used to happen there.
   */
  const scheduleFailedDismiss = useCallback(() => {}, []);

  const orphanStrikes = useRef({});   // jobId -> consecutive 404s; see the orphan sweep below

  /* WHAT HAS BEEN LOADED, AND FOR WHOM. The save effect below must not run until this one has
     hydrated the current user, or the first render's empty queue would be written over the
     stored one before it is read back. */
  const hydratedFor = useRef(null);

  /* LOAD — AND RE-LOAD WHENEVER THE SIGNED-IN USER CHANGES.
     This used to run once on mount with no idea who was signed in. Signing out and into
     another account is a route change, not a reload, so this hook never unmounted: the
     previous account's cards simply stayed in memory and on screen. Now a change of uid tears
     the old session's listeners down, empties the queue, and hydrates the new user's own.
     `userId` is the only dependency on purpose — the subscribe helpers are recreated on
     render (their deps include a callback App passes inline), and depending on them would
     reset the queue on every render. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Whatever the previous user was watching stops being watched.
    Object.keys(subscriptions.current).forEach((jobId) => {
      if (subscriptions.current[jobId]) subscriptions.current[jobId]();
    });
    subscriptions.current = {};
    Object.keys(timeouts.current).forEach((jobId) => {
      clearTimeout(timeouts.current[jobId]);
      clearInterval(timeouts.current[jobId]);   // project aggregation is an interval
    });
    timeouts.current = {};
    jobStatusRef.current = {};
    finalized.current = new Set();
    queueRef.current = [];
    projectDataRef.current = {};
    orphanStrikes.current = {};
    setQueue([]);
    hydratedFor.current = null;

    if (!userId) return;                        // nobody signed in: nothing to own a queue

    // The pre-scoping key cannot be attributed to anyone, so it is dropped rather than
    // adopted; other accounts' buckets age out.
    dropUnscoped(QUEUE_STORE);
    pruneForeign(QUEUE_STORE, userId, FOREIGN_QUEUE_TTL);

    const parsedQueue = readUserJSON(QUEUE_STORE, userId, null);
    hydratedFor.current = userId;
    if (Array.isArray(parsedQueue) && parsedQueue.length) {
      try {
        const now = Date.now();
        const FAILED_DISMISS = 60 * 1000;
        const FAILED_KEEP = 24 * 60 * 60 * 1000;
        const COMPLETED_DISMISS = 60 * 60 * 1000;

        let cleanedQueue = parsedQueue
          .filter(job => {
            // CANCELLED is a decision somebody already made and watched happen, so it still
            // ages out. FAILED is news they may not have seen — it survives a reload and
            // waits to be dismissed, up to a day, which is long enough to cover "I closed
            // the laptop and came back" without keeping last week's failure on screen.
            if (job.state === JOB_STATES.CANCELLED) {
              const finishedAt = job.completedAt || job.createdAt;
              return finishedAt && (now - new Date(finishedAt).getTime()) < FAILED_DISMISS;
            }
            if (job.state === JOB_STATES.FAILED) {
              const finishedAt = job.completedAt || job.createdAt;
              return finishedAt && (now - new Date(finishedAt).getTime()) < FAILED_KEEP;
            }
            if (job.state === JOB_STATES.COMPLETED) {
              const finishedAt = job.completedAt || job.createdAt;
              return finishedAt && (now - new Date(finishedAt).getTime()) < COMPLETED_DISMISS;
            }
            return true;
          })
          .map(job => {
            if (job.state === JOB_STATES.QUEUED || job.state === JOB_STATES.PROCESSING) {
              const age = now - new Date(job.createdAt).getTime();
              if (age > STALE_TIMEOUT) {
                return { ...job, state: JOB_STATES.FAILED, error: 'Generation timed out' };
              }
            }
            return job;
          });

        // Finalize jobs stuck between Phase 1 and Phase 2.
        //
        // KEPT, though the live path no longer strands anything here. It is still the
        // backstop for a tab closed between the snapshot and the finalizer, and removing a
        // net because the thing it caught was fixed is how the thing comes back.
        cleanedQueue = cleanedQueue.map(j =>
          j.backendCompleted && j.state !== JOB_STATES.COMPLETED
            ? { ...j, state: JOB_STATES.COMPLETED, completedAt: j.completedAt || new Date().toISOString() }
            : j
        );

        /* WHAT THIS SESSION MUST NOT DO AGAIN. `onJobCompleted` does work that is not
           idempotent — it preloads and pins a thumbnail — and a job the previous session
           already finalized comes back carrying `backendCompleted`. Seeding the ref with
           those ids is the across-a-reload half of the guard the old `alreadyFinalized`
           flag provided; the ref itself is the within-a-session half. */
        cleanedQueue.forEach((j) => {
          if (j.backendCompleted) finalized.current.add(j.id);
        });

        setQueue(cleanedQueue);

        // Re-subscribe to still-active jobs
        const activeJobs = cleanedQueue.filter(job =>
          job.state === JOB_STATES.QUEUED || job.state === JOB_STATES.PROCESSING
        );
        activeJobs.forEach(job => {
          // The persisted job carries its own `metadata.durationType`, so a reel that was
          // running when the tab was reloaded comes back on the reel clock rather than being
          // failed two minutes later by a page it had nothing to do with.
          setSilenceBudget(job.id, job.metadata?.durationType);
          if (job.type === 'project' && job.metadata?.subJobIds) {
            projectDataRef.current[job.id] = { subJobIds: job.metadata.subJobIds };
            subscribeToProject(job.id);
          } else {
            subscribeToJob(job.id);
          }
        });
      } catch (error) {
        console.error('Error loading queue from localStorage:', error);
      }
    }
  }, [userId]);

  // Keep the ref mirror in step. Same effect ordering as the save below — both just follow
  // `queue`, and neither may run before the load effect has hydrated this user.
  useEffect(() => { queueRef.current = queue; }, [queue]);

  // Save the queue under its owner. Never while signed out, and never before the load effect
  // above has run for this user.
  useEffect(() => {
    if (!userId || hydratedFor.current !== userId) return;
    writeUserJSON(QUEUE_STORE, userId, queue);
  }, [queue, userId]);

  /* ─── Orphan sweep ────────────────────────────────────────────────
     A JOB THE BACKEND HAS FORGOTTEN NEVER ENDS ON ITS OWN. Progress reaches this hook through
     a Firestore listener on the job document, so the client only ever learns what that
     document says — and if the process that owned the job dies (a dev server restarted, a
     worker instance replaced mid-render), the document is simply left reading `processing`
     and no further write is ever made to it. The listener waits, correctly, forever; the card
     sits in its overrun state breathing at the user. The only backstop was the 20-minute
     per-job safety timeout, which is far too long to read as anything but a hang. Confirmed
     locally: the API's queue reported `all_jobs: []` while three cards were still counting.

     So: ask the API directly. It is the one participant that knows whether the job still
     exists. A 404 means nobody is working on it and nobody will, which is a failure however
     it happened. Two strikes, because a single error can just be a dropped request.
     `orphanStrikes` is declared up with the other refs: the hydrate effect resets it, and a
     `const` sitting below its own reader is the shape of a temporal-dead-zone bug. */
  useEffect(() => {
    const sweep = setInterval(async () => {
      const now = Date.now();
      const suspects = queue.filter((j) =>
        (j.state === JOB_STATES.QUEUED || j.state === JOB_STATES.PROCESSING)
        && !j.id?.startsWith("temp-")            // not submitted yet; there is nothing to ask about
        && !j.devEmpty                            // local-only job; no backend has ever heard of it
        && now - new Date(j.createdAt).getTime() > ORPHAN_GRACE_MS);

      for (const job of suspects) {
        try {
          await apiService.getVideoStatus(job.id);
          delete orphanStrikes.current[job.id];   // it exists — whatever we saw was transient
        } catch (err) {
          const gone = err?.status === 404;
          orphanStrikes.current[job.id] = (orphanStrikes.current[job.id] || 0) + (gone ? 1 : 0);
          if (orphanStrikes.current[job.id] < 2) continue;
          delete orphanStrikes.current[job.id];
          unsubscribeFromJob(job.id);
          setQueue((prev) => prev.map((j) =>
            j.id === job.id && (j.state === JOB_STATES.QUEUED || j.state === JOB_STATES.PROCESSING)
              ? { ...j, state: JOB_STATES.FAILED, error: GENERIC_ERROR_MESSAGE,
                  completedAt: new Date().toISOString() }
              : j));
          scheduleFailedDismiss(job.id);
        }
      }
    }, ORPHAN_SWEEP_MS);
    return () => clearInterval(sweep);
  }, [queue, unsubscribeFromJob, scheduleFailedDismiss]);

  // Cleanup all subscriptions on unmount
  useEffect(() => {
    return () => {
      Object.keys(subscriptions.current).forEach(jobId => {
        if (subscriptions.current[jobId]) subscriptions.current[jobId]();
      });
      Object.keys(timeouts.current).forEach(jobId => {
        clearTimeout(timeouts.current[jobId]);
        clearInterval(timeouts.current[jobId]);
      });
    };
  }, []);

  // ─── Public API ────────────────────────────────────────────────────

  const addJob = useCallback((jobData) => {
    const isProject = jobData.type === 'project';

    const durationType = jobData.durationType ||
                         jobData.duration_type ||
                         jobData.metadata?.durationType ||
                         jobData.metadata?.duration_type ||
                         'short';

    const newJob = {
      id: jobData.jobId,
      /* A REACT KEY THAT NEVER CHANGES. `id` does: a job is added under a temp id the instant
         you hit generate, then swapped for the real one when the server answers, about a
         second later. Anything keyed on `id` therefore unmounted and remounted in that
         moment — the card was rebuilt from scratch, which restarted its "waiting" pulse
         mid-dip and read as a flash. This is whatever the first id was, kept for the life of
         the job, and it is only ever a key. */
      uid: jobData.jobId,
      type: jobData.type || 'video',
      source: jobData.source || null,
      title: jobData.title || 'Untitled Generation',
      silent: !!jobData.silent,
      state: JOB_STATES.QUEUED,
      progress: 0,
      currentStep: isProject ? 'Starting generation...' : 'Queued for processing...',
      createdAt: new Date().toISOString(),
      metadata: {
        ...jobData.metadata,
        durationType: durationType
      },
      ...(isProject && {
        completedCount: 0,
        failedCount: 0,
        totalCount: jobData.metadata?.subJobIds?.length || jobData.totalCount || 0,
      }),
    };

    // BEFORE the subscribe below, which arms the clock. Arming first would put the first two
    // minutes of every reel on the lesson clock.
    setSilenceBudget(newJob.id, durationType);

    setQueue(prevQueue => [...prevQueue, newJob]);

    if (isProject && jobData.metadata?.subJobIds?.length) {
      projectDataRef.current[newJob.id] = { subJobIds: jobData.metadata.subJobIds };
      subscribeToProject(newJob.id);
    } else {
      const isTempId = jobData.jobId && jobData.jobId.toString().startsWith('temp-');
      if (!isTempId) {
        subscribeToJob(newJob.id);
      }
    }

    return newJob.id;
  }, [subscribeToJob, subscribeToProject]);

  const removeJob = useCallback((jobId) => {
    unsubscribeFromJob(jobId);
    delete projectDataRef.current[jobId];
    setQueue(prevQueue => prevQueue.filter(job => job.id !== jobId));
  }, [unsubscribeFromJob]);

  /* A CANCEL THAT FAILED MUST NOT LOOK LIKE ONE THAT WORKED.
   *
   * Reported as "this stop button does not work, although it should" — and it was worse than
   * not working. The catch block used to do EXACTLY what the success path does: unsubscribe,
   * forget the project data, drop the row. So every refusal the server makes — 404 "Job not
   * found", 403 "You can only cancel your own jobs", 400 "Only queued or processing jobs can
   * be cancelled" — removed the job from the panel while it carried on generating, billing
   * and finishing. The one visible consequence of pressing Stop was losing the ability to
   * watch the thing you had just failed to stop.
   *
   * It also made the caller's error handling dead code: `ReelQueue.kill` has always caught
   * and displayed a message, and this function could never reject, so the message could never
   * appear. The reason was sitting in `console.error` the whole time.
   *
   * So: tear down only on success, and re-throw otherwise. The row staying put IS the
   * feedback — the job is still running, and the panel now says so. */
  const cancelJob = useCallback(async (jobId) => {
    const pd = projectDataRef.current[jobId];
    if (pd) {
      // A PROJECT IS CANCELLED ONLY IF ITS LEGS WERE. allSettled never rejects, so this
      // reported success for a project whose every sub-job was refused.
      const results = await Promise.allSettled(
        pd.subJobIds.map(id => apiService.cancelJob(id)));
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length === results.length) {
        throw failed[0].reason instanceof Error
          ? failed[0].reason
          : new Error(String(failed[0].reason || 'could not cancel this project'));
      }
      if (failed.length) {
        console.warn(`${failed.length}/${results.length} legs of ${jobId} would not cancel`);
      }
    } else {
      await apiService.cancelJob(jobId);     // rejects on a non-2xx; let it through
    }
    unsubscribeFromJob(jobId);
    delete projectDataRef.current[jobId];
    setQueue(prevQueue => prevQueue.filter(job => job.id !== jobId));
  }, [unsubscribeFromJob]);

  const clearCompleted = useCallback(() => {
    setQueue(prevQueue => prevQueue.filter(job =>
      job.state !== JOB_STATES.COMPLETED &&
      job.state !== JOB_STATES.FAILED &&
      job.state !== JOB_STATES.CANCELLED
    ));
  }, []);

  const updateJobId = useCallback((oldId, newId) => {
    setQueue(prevQueue =>
      prevQueue.map(job =>
        job.id === oldId ? { ...job, id: newId } : job
      )
    );

    // Unsubscribe from old ID
    unsubscribeFromJob(oldId);

    // Subscribe to new ID after brief delay for state to settle
    setTimeout(() => {
      subscribeToJob(newId);
    }, 100);
  }, [subscribeToJob, unsubscribeFromJob]);

  /* `subJobIds` IS OPTIONAL, and that is what lets the session id arrive early.
   *
   * A project's Firestore row is written before any video is queued, but this used to be the
   * only way to put its id on the queue entry — and it demanded the sub-job ids too, which do
   * not exist until every generation request has come back. So the id landed seconds late, and
   * for those seconds the project's own cover had nothing to link to. Called with just a
   * sessionId it records that and nothing else: the count stays whatever addJob was told, and
   * the aggregate polling is not started, because there is still nothing to poll. */
  const updateProjectJob = useCallback((jobId, { subJobIds, sessionId }) => {
    setQueue(prevQueue =>
      prevQueue.map(job =>
        job.id === jobId
          ? {
              ...job,
              totalCount: subJobIds ? subJobIds.length : job.totalCount,
              metadata: {
                ...job.metadata,
                ...(subJobIds ? { subJobIds } : {}),
                ...(sessionId ? { sessionId } : {}),
              },
            }
          : job
      )
    );
    if (!subJobIds) return;
    projectDataRef.current[jobId] = { subJobIds };
    subscribeToProject(jobId);
  }, [subscribeToProject]);

  /* The local "Empty" mode's whole implementation. A job that no backend knows about: it goes
     straight to processing, sits there for DEV_EMPTY_SECONDS, then completes with no video
     attached. `devEmpty` marks it so the orphan sweep leaves it alone (asking the API about a
     job that was never submitted would fail it instantly) and so the Workspace knows to render
     it as a blank tile rather than looking for a video that does not exist. */
  const addDevEmptyJob = useCallback((title) => {
    const id = `dev-empty-${Date.now()}`;
    const now = new Date().toISOString();
    setQueue((prev) => [...prev, {
      id, uid: id, type: 'video', title: title || 'Empty', devEmpty: true,
      state: JOB_STATES.PROCESSING, progress: 0, currentStep: 'Generating...',
      createdAt: now, processingStartedAt: now, metadata: { credits: 0 },
    }]);
    timeouts.current[id] = setTimeout(() => {
      setQueue((prev) => prev.map((j) => j.id === id
        ? { ...j, state: JOB_STATES.COMPLETED, completedAt: new Date().toISOString() }
        : j));
    }, DEV_EMPTY_SECONDS * 1000);
    return id;
  }, []);

  /* A JOB THAT FAILS ON PURPOSE, for looking at the failure UI without waiting for a real
     one. Carries the error text the Modal client actually returns, so the reason line is
     exercised by the same string production would produce — a simulation that fails with
     "test error" would prove nothing about the mapping in failureReasonFor. Local only; the
     picker entry that reaches this does not exist off localhost. */
  const addDevFailedJob = useCallback((title) => {
    const id = `dev-fail-${Date.now()}`;
    const now = new Date().toISOString();
    setQueue((prev) => [...prev, {
      id, uid: id, type: 'video', title: title || 'Simulated failure', devEmpty: true,
      state: JOB_STATES.PROCESSING, progress: 0, currentStep: 'Generating...',
      createdAt: now, processingStartedAt: now, metadata: { credits: 1 },
    }]);
    timeouts.current[id] = setTimeout(() => {
      setQueue((prev) => prev.map((j) => j.id === id ? {
        ...j,
        state: JOB_STATES.FAILED,
        completedAt: new Date().toISOString(),
        error: '5 scene(s) failed, all with the same error: No result from the render worker '
             + 'after 60+300s (2 attempts). The scene may have been waiting for a free GPU, '
             + 'or rendering longer than the budget. Modal call fc-01M5SIMULATED.',
      } : j));
    }, DEV_FAIL_SECONDS * 1000);
    return id;
  }, []);

  const updateJobThumbnail = useCallback((jobId, thumbnailData) => {
    setQueue(prev => prev.map(job =>
      job.id === jobId ? { ...job, ...thumbnailData } : job
    ));
  }, []);

  // Memoize the returned object so its identity is stable across renders where
  // the queue hasn't changed. All the callbacks below are already useCallback'd,
  // so in practice this only changes when `queue`/`isMinimized` change. Without
  // this, consumers (e.g. App's commonProps) re-render on every parent render.
  return useMemo(() => ({
    queue,
    isMinimized,
    setIsMinimized,
    addJob,
    addDevEmptyJob,
    addDevFailedJob,
    removeJob,
    updateJobId,
    updateProjectJob,
    updateJobThumbnail,
    cancelJob,
    clearCompleted
  }), [
    queue,
    isMinimized,
    setIsMinimized,
    addJob,
    addDevEmptyJob,
    addDevFailedJob,
    removeJob,
    updateJobId,
    updateProjectJob,
    updateJobThumbnail,
    cancelJob,
    clearCompleted
  ]);
};
